/**
 * Jotai Provider Setup
 * Configures Jotai with Tanstack Query integration
 */

import { Provider, getDefaultStore } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClient } from './query-client';

interface JotaiProviderProps {
  children: React.ReactNode;
}

function HydrateAtoms({ children }: { children: React.ReactNode }) {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
}

/**
 * ONE store, explicitly the DEFAULT one.
 *
 * `<Provider>` with no `store` creates its own, and code outside React reaches the store
 * through `getDefaultStore()` — a different instance. Atom state then splits in two: every
 * component writes to the provider's, every service reads the default's, and nothing
 * reconciles them.
 *
 * `selected-business-unit-kim-ai` is where that showed. `useBusinessUnit` validates the
 * stored brand against the account and writes the correction back — into the provider's
 * store. `integration-scope`'s `currentBuId` read the default store, never saw it, and sent
 * the uncorrected value. On dev a connect on account 1 went out carrying brand 347, which
 * belongs to account 417, and machine answered "Business unit not found for this account".
 *
 * Naming the default store makes the two the same object, so a write through React is a
 * write the services see. `atomWithStorage` hid this for years: both stores hydrate the
 * same localStorage key, so they agree right up until one of them is written to.
 */
export function JotaiProvider({ children }: JotaiProviderProps) {
  return (
    <Provider store={getDefaultStore()}>
      <HydrateAtoms>{children}</HydrateAtoms>
    </Provider>
  );
}
